// Services.js
import React, { useState, useEffect } from 'react';
import SoftwareTab from '../tabs/SoftwareTab';
import StaffingTab from '../tabs/StaffingTab';
import ApproachSection from '../tabs/ApproachSection';
import DataScienceTab from '../tabs/DataScienceTab';
import ConsultingTab from '../tabs/ConsultingTab';

const emergingServiceContent = {
  'AI ML': {
    title: 'AI & ML Solutions',
    intro: 'Practical AI solutions that help teams automate decisions, surface insights, and improve customer and operational workflows.',
    services: [
      {
        title: 'AI Readiness & Strategy',
        description: 'Assess high-value opportunities, data readiness, risk, and implementation paths before investing in AI delivery.'
      },
      {
        title: 'Machine Learning Models',
        description: 'Build predictive models for forecasting, classification, recommendation, and process optimization.'
      },
      {
        title: 'Intelligent Automation',
        description: 'Use AI-assisted workflows to reduce manual effort across support, operations, reporting, and back-office teams.'
      }
    ]
  },
  Cybersecurity: {
    title: 'Cybersecurity',
    intro: 'Security guidance and implementation support that protects systems, data, and business continuity.',
    services: [
      {
        title: 'Security Assessments',
        description: 'Identify gaps across applications, cloud environments, access controls, and operational practices.'
      },
      {
        title: 'Cloud & Application Security',
        description: 'Strengthen architecture, secure deployment pipelines, and reduce exposure in modern software environments.'
      },
      {
        title: 'Governance Support',
        description: 'Create policies, controls, and response practices that help teams operate securely and consistently.'
      }
    ]
  },
  Coaching: {
    title: 'Coaching & Transformation',
    intro: 'Hands-on coaching for technical teams, leaders, and organizations navigating technology-led change.',
    services: [
      {
        title: 'Agile Delivery Coaching',
        description: 'Improve planning, execution, feedback loops, and team collaboration across product and engineering groups.'
      },
      {
        title: 'Technology Leadership',
        description: 'Support leaders with roadmap planning, delivery governance, and practical decision-making frameworks.'
      },
      {
        title: 'Team Enablement',
        description: 'Build skills through workshops, mentoring, and structured learning paths tailored to current initiatives.'
      }
    ]
  }
};

const solutionHighlights = [
  {
    title: 'Custom Software',
    outcome: 'Automate your unique workflows.',
    description: 'Replace manual steps and disconnected tools with software built around how your business actually operates.'
  },
  {
    title: 'Cloud Migration',
    outcome: 'Scale your infrastructure with less disruption.',
    description: 'Modernize systems, improve reliability, and control cloud costs with a roadmap your team can trust.'
  },
  {
    title: 'Specialist Staffing',
    outcome: 'Fill skill gaps with vetted experts.',
    description: 'Bring in experienced technical talent for delivery, data, cloud, security, and transformation initiatives.'
  }
];

const Services = ({ initialTab = 'Software' }) => {
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  const renderTabContent = () => {
    const emergingContent = emergingServiceContent[activeTab];

    if (emergingContent) {
      return (
        <>
          <div>
            <h2 style={{ fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
              {emergingContent.title}
            </h2>
            <p style={{ fontSize: '1rem', marginBottom: '2rem', color: '#475569' }}>
              {emergingContent.intro}
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', justifyContent: 'center' }}>
              {emergingContent.services.map(service => (
                <article
                  key={service.title}
                  style={{
                    backgroundColor: '#ffffff',
                    padding: '1.5rem',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    flex: '1 1 300px',
                    maxWidth: '360px'
                  }}
                >
                  <h3 style={{ fontWeight: '600', marginTop: 0 }}>{service.title}</h3>
                  <p style={{ fontSize: '0.95rem', color: '#475569', lineHeight: '1.6' }}>
                    {service.description}
                  </p>
                  <a href="#contact" style={{ color: '#1e3a8a', fontWeight: '500', fontSize: '0.9rem' }}>
                    Inquire about this service
                  </a>
                </article>
              ))}
            </div>
          </div>
          <ApproachSection />
        </>
      );
    }

    switch (activeTab) {
      case 'Software':
        return <><SoftwareTab /><ApproachSection /></>;
      case 'Staffing':
        return <><StaffingTab /><ApproachSection /></>;
      case 'Consulting':
        return <><ConsultingTab /><ApproachSection /></>;
      case 'Data Science':
        return <><DataScienceTab /><ApproachSection /></>;
      default:
        return null;
    }
  };

  return (
    <section id="services">
      <div style={{
        background: 'linear-gradient(to right, #0f172a, #1e3a8a)',
        padding: '4rem 2rem',
        color: 'white',
        textAlign: 'center'
      }}>
        <h1 style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>Solutions Built Around Business Outcomes</h1>
        <p style={{ marginTop: '1rem' }}>
          We help you automate work, modernize infrastructure, protect data, and add the right expertise when your roadmap needs momentum.
        </p>
      </div>

      <div style={{ backgroundColor: '#f9f9f9', padding: '3rem 2rem', paddingBottom: '6rem' }}>
        <div className="solution-highlight-grid">
          {solutionHighlights.map(solution => (
            <article key={solution.title} className="solution-highlight-card">
              <h2>{solution.title}</h2>
              <p className="solution-outcome">{solution.outcome}</p>
              <p>{solution.description}</p>
            </article>
          ))}
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: '0.5rem',
          marginBottom: '2rem'
        }}>
          {["Software", "AI ML", "Data Science", "Cybersecurity", "Coaching", "Consulting", "Staffing"].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
                backgroundColor: activeTab === tab ? '#e0e7ff' : '#f0f0f0',
                fontWeight: activeTab === tab ? '600' : '500',
                color: '#1e293b'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {renderTabContent()}
      </div>
    </section>
  );
};

export default Services;
